/* Ads and donations, chosen at build time like analytics.js. Supercell's Fan
   Content Policy allows exactly these two: ads, and donations that unlock
   nothing. Everything here is optional; with nothing set the site renders no
   ad slots and no donate link.

   REACT_APP_AD_PROVIDER     none (default) | adsense
   REACT_APP_AD_CLIENT       network publisher id (AdSense: ca-pub-XXXXXXXXXXXXXXXX)
   REACT_APP_AD_SLOT_INLINE  ad unit id for slots between page sections
   REACT_APP_AD_SLOT_FOOTER  ad unit id for the slot above the site footer
   REACT_APP_DONATION_URL    e.g. https://ko-fi.com/<handle>
   REACT_APP_CONTACT_EMAIL   shown on /privacy

   A slot whose unit id is empty is not rendered at all. To add another
   network, add an entry to PROVIDERS: a script URL and a mount() that fills
   the fixed-size box it is given. */

const env = (k) => (process.env[k] || '').trim();

export const donationUrl = env('REACT_APP_DONATION_URL');
export const contactEmail = env('REACT_APP_CONTACT_EMAIL');

const providerName = env('REACT_APP_AD_PROVIDER').toLowerCase() || 'none';
const client = env('REACT_APP_AD_CLIENT');
const UNITS = {
  inline: env('REACT_APP_AD_SLOT_INLINE'),
  footer: env('REACT_APP_AD_SLOT_FOOTER'),
};

const PROVIDERS = {
  adsense: {
    label: 'Google AdSense',
    privacyUrl: 'https://policies.google.com/technologies/partner-sites',
    optOutUrl: 'https://adssettings.google.com',
    scriptSrc: () =>
      `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`,
    // Fixed-size unit (no data-ad-format): the network fills the box CSS gives
    // it and never resizes it, so the page can't shift when the ad arrives.
    mount(box, unit) {
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.setAttribute('data-ad-client', client);
      ins.setAttribute('data-ad-slot', unit);
      box.appendChild(ins);
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    },
  },
};

export const adProvider = client ? PROVIDERS[providerName] || null : null;
export const adsEnabled = Boolean(adProvider && Object.values(UNITS).some(Boolean));

export function adUnit(slot) {
  return adProvider ? UNITS[slot] || '' : '';
}

/* Ads never load in development: clicks and impressions from a dev server
   count as invalid traffic and can get the publisher account banned. */
export const adsLive = process.env.NODE_ENV === 'production';

/* One network script per page load, fetched only once the page itself has
   finished loading and the browser is idle, so it never competes with the
   stats for LCP or the main thread. */
let scriptPromise = null;
export function loadAdScript() {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const inject = () => {
        const s = document.createElement('script');
        s.async = true;
        s.src = adProvider.scriptSrc();
        s.crossOrigin = 'anonymous';
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      };
      const whenIdle = () =>
        window.requestIdleCallback ? window.requestIdleCallback(inject, { timeout: 3000 }) : setTimeout(inject, 1);
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    });
  }
  return scriptPromise;
}

/* Reopens the consent message of a Google-certified CMP (AdSense's own
   "Privacy & messaging" one). Returns false if none has loaded, e.g. for
   visitors outside the regions the message is shown in. */
export function openConsentChoices() {
  const fc = window.googlefc;
  if (!fc || typeof fc.showRevocationMessage !== 'function') return false;
  fc.callbackQueue = fc.callbackQueue || [];
  fc.callbackQueue.push(fc.showRevocationMessage);
  return true;
}
